/**
 * «Журнал взаємодії з MMS» — три блоки.
 *
 * Блок 1 — версії графіка (Первинна / Уточнена / Остаточна) з двома незалежними
 *          статусами: часовий стан версії та технічний статус XML-файлу.
 * Блок 2 — стрічка подій обраної версії: Сформовано → Передано → Прийнято →
 *          Оброблено → Статус.
 * Блок 3 — дії над обраною версією.
 *
 * Дату поставки бере зі спільного селектора сторінки: журнал і прогноз завжди
 * говорять про ту саму добу, тож окремий календар лише створював би розсинхрон.
 *
 * Активну версію та дедлайни рахує БЕКЕНД у Europe/Kyiv — годиннику браузера ця
 * логіка не довіряється (сервер працює в UTC, а перехід на зимовий час зсуває
 * дедлайни на годину).
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  Activity, AlertCircle, AlertTriangle, Archive, CheckCircle2, ChevronDown, ChevronRight,
  Download, EyeOff, FileCode2, Layers, Loader2, Lock, PenLine, Save, Send, Trash2, Unlock, X,
  type LucideIcon,
} from 'lucide-react';
import { useFcmmsTheme } from '../theme';

interface StatusChip { code: string; label: string; dot: string }
interface Version {
  revision: number;
  name: string;
  deadline: string;
  deadlineLabel: string;
  isActive: boolean;
  timeState: StatusChip;
  techStatus: StatusChip;
  scheduleId: string | null;
  totalMwh: number | null;
  hasXml: boolean;
  hasAssetSplit: boolean;
  entryMode: string | null;
  submittedBy: string | null;
  errorMessage: string | null;
  events: {
    generated: string | null; submitted: string | null; received: string | null;
    processed: string | null; status: string | null;
  };
  actions: { canGenerate: boolean; canDownload: boolean; canSubmit: boolean; canEnterManually: boolean };
}
interface JournalResp {
  assetId: string; date: string; activeRevision: number | null; allClosed: boolean;
  statuses: { initial: string; ok: string };
  versions: Version[];
}

interface SavedVersion {
  revision: number; status: string; hasXml: boolean; hasMine: boolean;
  /** false, якщо графік уже відправляли — такий запис лишається як слід взаємодії. */
  canDelete: boolean;
  myTotalKwh: number | null; groupTotalMwh: number | null;
  savedBy: string | null; savedAt: string | null; submittedAt: string | null; mmsStatus: string | null;
}
interface SavedRecord { date: string; xCode: string; versions: SavedVersion[] }
interface RecordsResp {
  assetId: string; assetName: string | null; xCode: string | null;
  groupAssets: string | null; groupSize?: number; records: SavedRecord[];
}

interface Props {
  assetId: string;
  date: string;
  canEdit: boolean;
  /** Версія, яку зараз дивиться сторінка (керується ззовні, щоб таблиця й журнал не розʼїхались). */
  selectedRevision: number | null;
  onSelectRevision: (rev: number) => void;
  /** Перехід до збереженого запису: змінює і добу, і версію. */
  onPickRecord: (date: string, rev: number) => void;
  /** Повідомляє сторінці, яка версія активна і які ще відкриті для правок. */
  onMeta?: (m: { activeRevision: number | null; openRevisions: number[] }) => void;
  /** Зростає після кожного збереження на сторінці — змушує перечитати журнал і список. */
  refreshTick?: number;
  onChanged?: () => void;
}

// Кольори — лише токени (§1 контракту). Компонент рендерить HTML, тож
// var(--…) читається напряму: getComputedStyle потрібен тільки для SVG-серій.
const BRAND = 'var(--series-3)';
const OK = 'var(--ok)';

// techColor uses a runtime DIM value — must be a function taking dim param.
const techColor = (code: string, dim: string) =>
  code === 'error' ? 'var(--err)' : code === 'confirmed' ? OK : code === 'received' ? 'var(--text-muted)'
    : code === 'submitted' ? 'var(--series-3)' : code === 'generated' ? 'var(--warn)' : dim;

// Людський розбір відповіді Укренерго: HTTP-код + текст SOAP-fault
// (Reason/Text | faultstring); повний XML лишаємо під тоглом.
function parseMmsError(raw: string): { reason: string; http: string | null } {
  if (!raw) return { reason: '', http: null };
  const http = (raw.match(/HTTP\s+(\d{3})/) || [])[1] || null;
  // Не вимагаємо закривного тега — у БД повідомлення інколи обрізане.
  const m = raw.match(/<(?:\w+:)?(?:Text|faultstring)[^>]*>([^<]+)/i)
        || raw.match(/<(?:\w+:)?Reason[^>]*>\s*([^<]+)/i);
  let reason = m ? m[1].trim() : raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  reason = reason.replace(/^HTTP\s+\d{3}:\s*/i, '').slice(0, 300);  // прибрати дубль HTTP-префікса
  return { reason, http };
}

export default function MmsJournal({
  assetId, date, canEdit, selectedRevision, onSelectRevision, onPickRecord, onMeta, onChanged, refreshTick,
}: Props) {
  const { t } = useTranslation();
  const T = useFcmmsTheme();
  const [data, setData] = useState<JournalResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<RecordsResp | null>(null);
  const [openDates, setOpenDates] = useState<Record<string, boolean>>({});
  // Видалення підтверджується другим натисканням на ту саму кнопку — окреме
  // модальне вікно тут зайве, а випадковий клік не має нічого стирати.
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [form, setForm] = useState<{ submittedAt: string; receivedAt: string; statusMessageAt: string; mmsStatus: string }>(
    { submittedAt: '', receivedAt: '', statusMessageAt: '', mmsStatus: '' });

  const load = useCallback(() => {
    if (!assetId || !date) { setLoading(false); return; }
    setLoading(true);
    authFetch(`${API_BASE_URL}/dispatch/forecast-mms/journal?assetId=${assetId}&date=${date}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: JournalResp) => {
        setData(d);
        if (selectedRevision == null) onSelectRevision(d.activeRevision ?? 1);
        onMeta?.({
          activeRevision: d.activeRevision,
          openRevisions: d.versions.filter((v) => v.timeState.code === 'now').map((v) => v.revision),
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, date, refreshTick]);

  const loadRecords = useCallback(() => {
    if (!assetId) return;
    authFetch(`${API_BASE_URL}/dispatch/forecast-mms/mms-records?assetId=${assetId}&limit=60`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: RecordsResp) => setRecords(d))
      .catch(() => setRecords(null));
  }, [assetId]);
  useEffect(() => { loadRecords(); }, [loadRecords, date, refreshTick]);

  useEffect(() => { setMsg(null); setShowManual(false); setConfirmDel(null); }, [assetId, date]);
  useEffect(() => { load(); }, [load]);

  const current = useMemo(
    () => data?.versions.find((v) => v.revision === (selectedRevision ?? data?.activeRevision ?? 1)) ?? null,
    [data, selectedRevision]);

  const call = async (label: string, fn: () => Promise<void>) => {
    setBusy(label); setMsg(null);
    try { await fn(); } catch (e: any) { setMsg({ kind: 'err', text: e?.message || t('ak_dsp3_error') }); }
    finally { setBusy(null); }
  };

  const generate = () => call('gen', async () => {
    const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/xml/generate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId, date, revision: current?.revision }),
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b?.message || b?.error || `HTTP ${r.status}`);
    setMsg({ kind: 'ok', text: t('ak_dsp3_xml_generated_mwh_split_across_assets', { mwh: b.totalEnergyMwh, n: b.assetsInSplit }) });
    load(); loadRecords(); onChanged?.();
  });

  const submit = () => call('submit', async () => {
    const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/xml/submit`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId, date, revision: current?.revision }),
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b?.message || b?.error || `HTTP ${r.status}`);
    setMsg({ kind: 'ok', text: t('ak_dsp3_the_schedule_has_been_queued_for_submi') });
    load(); loadRecords(); onChanged?.();
    // Хост-сабмітер (mms_submit.py) виконує mTLS SOAP POST кожні ~2 хв; опитуємо журнал.
    for (let i = 0; i < 8; i++) {
      await new Promise((r2) => setTimeout(r2, 20000));
      load(); loadRecords();
    }
  });

  const download = () => call('dl', async () => {
    const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/xml/download?assetId=${assetId}&date=${date}&revision=${current?.revision}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mms_${date}_v${current?.revision}.xml`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  });

  const removeVersion = (d: string, rev: number) => call(`del-${d}-${rev}`, async () => {
    const r = await authFetch(
      `${API_BASE_URL}/dispatch/forecast-mms/schedule?assetId=${assetId}&date=${d}&revision=${rev}`,
      { method: 'DELETE' });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b?.message || b?.error || `HTTP ${r.status}`);
    setConfirmDel(null);
    setMsg({ kind: 'ok', text: t('ak_dsp3_version_for_deleted', { rev: rev, d: d.split('-').reverse().join('.') }) });
    load(); loadRecords(); onChanged?.();
  });

  const saveManual = () => call('manual', async () => {
    const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/journal/manual`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId, date, revision: current?.revision, ...form }),
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b?.message || b?.error || `HTTP ${r.status}`);
    setMsg({ kind: 'ok', text: t('ak_dsp3_journal_entry_updated') });
    setShowManual(false); load(); loadRecords(); onChanged?.();
  });

  // Кнопка за стандартом хедер-контролів §6: 36 / radius 10.
  // Порожній label робить кнопку квадратною (36×36) — значення несе іконка,
  // а повний підпис лишається в title та aria-label. Стан «виконується»
  // замінює іконку крутілкою, а не текстом: інакше кнопка стрибала б по
  // ширині щоразу, коли починається дія.
  const btn = (label: string, onClick: () => void,
               opts: { disabled?: boolean; primary?: boolean; busy?: boolean; title?: string; Icon?: LucideIcon } = {}) => (
    <button onClick={onClick} disabled={opts.disabled} title={opts.title} aria-label={opts.title ?? label}
      className={`text-xs transition-colors whitespace-nowrap inline-flex items-center justify-center gap-1.5 shrink-0 ${label ? 'px-3' : ''}`}
      style={{
        height: 36, width: label ? undefined : 36, borderRadius: 10,
        background: opts.primary && !opts.disabled ? BRAND : 'var(--surface-2)',
        color: opts.disabled ? 'var(--text-dim)' : (opts.primary ? '#fff' : 'var(--text)'),
        border: `1px solid ${opts.primary && !opts.disabled ? BRAND : 'var(--line)'}`,
        cursor: opts.disabled ? 'not-allowed' : 'pointer', opacity: opts.disabled ? 0.55 : 1,
      }}>
      {opts.busy
        ? <Loader2 size={14} strokeWidth={2} className="animate-spin" />
        : opts.Icon && <opts.Icon size={14} strokeWidth={2} />}
      {label}
    </button>
  );

  const EVENTS: { key: keyof Version['events']; title: string }[] = [
    { key: 'generated', title: t('ak_dsp3_generated') },
    { key: 'submitted', title: t('ak_dsp3_submitted_to_mms') },
    { key: 'received', title: t('ak_dsp3_received_by_mms') },
    { key: 'processed', title: t('ak_dsp3_processed_by_mms') },
    { key: 'status', title: t('ak_dsp3_status') },
  ];

  /** Індикатор лише на ПЕРШОМУ завантаженні — далі дані вже є на екрані. */
  const firstLoad = loading && !data;
  const delKey = (d: string, rev: number) => `${d}#${rev}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>
          {t('ak_dsp3_mms_interaction_log')}
        </h3>
        <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>
          {t('ak_dsp3_delivery_day_kyiv_time', { d: date.split('-').reverse().join('.') })}
        </span>
      </div>

      {msg && (
        <div className="tile p-3 text-xs flex items-start gap-2"
          style={{ color: msg.kind === 'ok' ? 'var(--ok)' : 'var(--warn)' }}>
          {msg.kind === 'ok'
            ? <CheckCircle2 size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
            : <AlertTriangle size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />}
          <span>{msg.text}</span>
        </div>
      )}

      {firstLoad ? (
        <div className="tile p-4 text-xs py-10 text-center" style={{ color: 'var(--text-muted)' }}>{t('ak_dsp3_loading')}</div>
      ) : !data ? (
        <div className="tile p-4 text-xs py-10 text-center" style={{ color: 'var(--text-muted)' }}>
          {t('ak_dsp3_no_journal_data_for_this_station')}
        </div>
      ) : (
        <>
          {/* ── БЛОК 1: версії ──────────────────────────────────────────
              Було сіткою карток із власним контуром кожна — це «плитка на
              плитці». Тепер список рядків у межах ОДНІЄЇ плитки: розділювач —
              тонка лінія, вибір позначає акцентна смуга зліва, а не рамка. */}
          <div className="tile p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Layers size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
              <h4 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{t('ak_dsp3_schedule_versions')}</h4>
            </div>
            <div className="flex flex-col">
              {data.versions.map((v, vi) => {
                const chosen = v.revision === (selectedRevision ?? data.activeRevision);
                // Дедлайн приходить як «ДД.ММ.РРРР ГГ:ХВ», але година вже є в
                // підписі («до 10:00 D-1»), тож у рядку лишаємо саму дату.
                const deadlineDate = (v.deadline || '').split(' ')[0];
                const open = v.timeState.code === 'now';
                const bar = v.isActive ? 'var(--ok)' : chosen ? 'var(--series-3)' : 'transparent';
                return (
                  <button key={v.revision} onClick={() => onSelectRevision(v.revision)}
                    title={`${v.name} · ${v.timeState.label}`}
                    className="text-left flex items-center gap-3 py-2.5 pl-2.5 cursor-pointer"
                    style={{
                      borderTop: vi > 0 ? '1px solid var(--line)' : undefined,
                      borderLeft: `3px solid ${bar}`,
                    }}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold" style={{ color: 'var(--text)' }}>
                        {t('ak_dsp3_version', { n: v.revision })}
                        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {v.deadlineLabel} · {deadlineDate}</span>
                      </div>
                      <span className="text-[11px] inline-flex items-center gap-1.5 mt-1"
                        style={{ color: techColor(v.techStatus.code, 'var(--text-dim)') }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                                       background: techColor(v.techStatus.code, 'var(--text-dim)') }} />
                        {v.techStatus.label}
                      </span>
                    </div>
                    {v.totalMwh != null && (
                      <span className="font-display text-[13px] shrink-0" style={{ color: 'var(--text)' }}>
                        {v.totalMwh}<span className="text-[9.5px] ml-1" style={{ color: 'var(--text-muted)' }}>{t('ak_dsp3_mwh')}</span>
                      </span>
                    )}
                    {/* часовий стан версії: відкрита для правок чи вже закрита */}
                    {open
                      ? <Unlock size={13} strokeWidth={2} style={{ color: 'var(--ok)', flexShrink: 0 }} />
                      : <Lock size={13} strokeWidth={2} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── БЛОК 2: стрічка подій обраної версії ────────────────────
              Було п'ять боксів із рамками всередині панелі. Тепер — прості
              пари «підпис / значення» в сітці, розділені відступом. */}
          <div className="tile p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
              <h4 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>
                {t('ak_dsp3_progress_version', { n: current?.revision })} · {current?.name}
              </h4>
            </div>
            <div className="grid gap-x-4 gap-y-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
              {EVENTS.map((e) => {
                const val = current?.events[e.key] || null;
                const isStatus = e.key === 'status';
                // Комірка «Статус»: якщо немає mms_status — показуємо тех-статус
                // (Помилка / Передано / Прийнято…) з відповідним кольором.
                const tech = current?.techStatus;
                const display = isStatus ? (val || tech?.label || '—') : (val || '—');
                const color = (display === '—') ? 'var(--text-dim)'
                  : isStatus ? (val === data.statuses.ok ? 'var(--ok)' : techColor(tech?.code || '', 'var(--text)'))
                    : 'var(--text)';
                return (
                  <div key={e.key} className="min-w-0">
                    <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{e.title}</div>
                    <div className="font-display text-[13.5px] mt-1 truncate" style={{ color }}>
                      {display}
                    </div>
                  </div>
                );
              })}
            </div>
            {current?.errorMessage && (() => {
              const { reason, http } = parseMmsError(current.errorMessage);
              return (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--line)' }}>
                  <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>{t('ak_dsp3_ukrenergo_response')}</div>
                  <div className="text-[12px] flex items-start gap-1.5" style={{ color: 'var(--err)' }}>
                    <AlertCircle size={13} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
                    <span>{http ? `HTTP ${http} · ` : ''}{reason}</span>
                  </div>
                  <details className="mt-1.5">
                    <summary className="text-[10px] cursor-pointer" style={{ color: 'var(--text-dim)' }}>{t('ak_dsp3_show_xml_response')}</summary>
                    <pre className="text-[10px] mt-1 p-2 rounded overflow-auto whitespace-pre-wrap"
                      style={{ color: 'var(--text-dim)', background: 'var(--surface-2)', maxHeight: 180 }}>{current.errorMessage}</pre>
                  </details>
                </div>
              );
            })()}
          </div>

          {/* ── БЛОК 3: дії ────────────────────────────────────────────── */}
          <div className="tile p-4 sm:p-5">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Підпис лишився тільки там, де іконка сама не каже, ЩО саме
                  робиться: «XML» на формуванні й «MMS» на відправці.
                  Відправка лишається брендовою — це єдина дія тут, що виходить
                  назовні, до Укренерго, і скасувати її не можна. */}
              {btn('XML', generate,
                { disabled: !canEdit || !current?.actions.canGenerate || !!busy, Icon: FileCode2, busy: busy === 'gen',
                  title: current?.actions.canGenerate ? t('ak_dsp3_generate_xml_build_the_schedule_from_t') : t('ak_dsp3_available_for_the_active_version_only') })}
              {btn('', download,
                { disabled: !current?.actions.canDownload || !!busy, Icon: Download, busy: busy === 'dl',
                  title: t('ak_dsp3_download_xml') })}
              {btn('MMS', submit,
                { disabled: !canEdit || !current?.hasXml || !!busy, Icon: Send, primary: true, busy: busy === 'submit',
                  title: current?.hasXml ? t('ak_dsp3_send_to_mms_queue_the_schedule_and_sub') : t('ak_dsp3_generate_the_xml_first') })}
              {btn('', () => setShowArchive((v) => !v),
                { Icon: showArchive ? EyeOff : Archive,
                  title: showArchive ? t('ak_dsp3_hide_xml_archive') : t('ak_dsp3_xml_archive') })}
              {canEdit && btn('', () => setShowManual((v) => !v),
                { disabled: !current?.hasXml, Icon: showManual ? X : PenLine,
                  title: !current?.hasXml ? t('ak_dsp3_generate_the_xml_first')
                    : showManual ? t('ak_dsp3_collapse_manual_entry') : t('ak_dsp3_enter_manually_record_mms_times_and_st') })}
            </div>
            <div className="text-[11px] mt-3 pt-3" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
              {t('ak_dsp3_send_to_mms_submits_the_generated_xml')}
            </div>
          </div>

          {/* ── ручне внесення ─────────────────────────────────────────── */}
          {showManual && current && (
            <div className="tile p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-3">
                <PenLine size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
                <h4 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>
                  {t('ak_dsp3_manual_entry_version', { n: current.revision })}
                </h4>
              </div>
              <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
                {([
                  ['submittedAt', t('ak_dsp3_time_sent_to_mms')],
                  ['receivedAt', t('ak_dsp3_time_received_by_mms')],
                  ['statusMessageAt', t('ak_dsp3_status_message_time')],
                ] as [keyof typeof form, string][]).map(([k, title]) => (
                  <label key={k} className="flex flex-col gap-1">
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{title}</span>
                    <input type="datetime-local" value={form[k]}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      className="px-2.5 text-xs outline-none"
                      style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)',
                               border: '1px solid var(--line)', color: 'var(--text)', colorScheme: T.inputScheme }} />
                  </label>
                ))}
                <label className="flex flex-col gap-1">
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{t('ak_dsp3_mms_status')}</span>
                  <select value={form.mmsStatus}
                    onChange={(e) => setForm((f) => ({ ...f, mmsStatus: e.target.value }))}
                    className="px-2.5 text-xs outline-none"
                    style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)',
                             border: '1px solid var(--line)', color: 'var(--text)', colorScheme: T.inputScheme }}>
                    <option value="">{t('ak_dsp3_not_set')}</option>
                    <option value={data.statuses.initial}>{data.statuses.initial}</option>
                    <option value={data.statuses.ok}>{data.statuses.ok}</option>
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {btn(busy === 'manual' ? t('ak_dsp3_saving') : t('ak_dsp3_save_entry'), saveManual, { primary: true, disabled: !!busy, Icon: Save })}
                <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{t('ak_dsp3_an_empty_field_means_no_change_time_is')}</span>
              </div>
            </div>
          )}

          {/* ── збережені записи графіків ───────────────────────────────
              Було три рівні вкладених рамок (панель → дата → версія). Тепер
              один рівень: доби розділені лініями, версії — відступом і
              акцентною смугою на обраній. */}
          {records && records.records.length > 0 && (
            <div className="tile p-4 sm:p-5">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Archive size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
                  <h4 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{t('ak_dsp3_saved_schedules')}</h4>
                </div>
                <div className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                  EIC {records.xCode}{records.groupAssets ? ` · ${records.groupAssets}` : ''}
                </div>
              </div>

              <div className="flex flex-col" style={{ maxHeight: 300, overflowY: 'auto' }}>
                {records.records.map((rec, ri) => {
                  const open = !!openDates[rec.date];
                  const multi = rec.versions.length > 1;
                  const isHere = rec.date === date;
                  return (
                    <div key={rec.date} style={{ borderTop: ri > 0 ? '1px solid var(--line)' : undefined }}>
                      <button
                        onClick={() => multi
                          ? setOpenDates((o) => ({ ...o, [rec.date]: !o[rec.date] }))
                          : onPickRecord(rec.date, rec.versions[0].revision)}
                        className="w-full text-left py-2 flex items-center gap-2 cursor-pointer">
                        {multi
                          ? (open
                            ? <ChevronDown size={14} strokeWidth={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            : <ChevronRight size={14} strokeWidth={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />)
                          : <span style={{ width: 14, flexShrink: 0 }} />}
                        <span className="text-xs font-semibold" style={{ color: isHere ? 'var(--text)' : 'var(--text-muted)' }}>
                          {rec.date.split('-').reverse().join('.')}
                        </span>
                        <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{rec.xCode}</span>
                        <span className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{records.groupAssets}</span>
                        <span className="ml-auto text-[10px]" style={{ color: 'var(--text-dim)' }}>
                          {multi ? t('ak_dsp3_versions', { n: rec.versions.length }) : t('ak_dsp3_version_2', { n: rec.versions[0].revision })}
                        </span>
                      </button>

                      {(open || !multi) && (
                        <div className="flex flex-col pb-1.5">
                          {rec.versions.map((v) => {
                            const picked = isHere && selectedRevision === v.revision;
                            const confirming = confirmDel === delKey(rec.date, v.revision);
                            return (
                              <div key={v.revision}
                                onClick={() => onPickRecord(rec.date, v.revision)}
                                className="w-full text-left py-1.5 pl-3 ml-3.5 flex items-center gap-2 flex-wrap cursor-pointer"
                                style={{ borderLeft: `3px solid ${picked ? 'var(--ok)' : 'var(--line)'}` }}>
                                <span className="text-[11px] font-semibold" style={{ color: 'var(--text)' }}>
                                  {t('ak_dsp3_version', { n: v.revision })}
                                </span>
                                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{v.status}</span>
                                {v.myTotalKwh != null && (
                                  <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                                    {t('ak_dsp3_your_solar_plant_kwh', { n: v.myTotalKwh.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA') })}
                                  </span>
                                )}
                                {v.mmsStatus && <span className="text-[10px]" style={{ color: 'var(--ok)' }}>{v.mmsStatus}</span>}
                                <span className="ml-auto text-[10px]" style={{ color: 'var(--text-dim)' }}>
                                  {v.savedAt || '—'}{v.savedBy ? ` · ${v.savedBy}` : ''}
                                </span>

                                {canEdit && (v.canDelete ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const k = delKey(rec.date, v.revision);
                                      if (confirmDel === k) removeVersion(rec.date, v.revision);
                                      else setConfirmDel(k);
                                    }}
                                    disabled={busy === `del-${rec.date}-${v.revision}`}
                                    title={(records?.groupSize || 1) > 1
                                      ? t('ak_dsp3_the_schedule_is_submitted_for_group_de', { x: records?.xCode, a: records?.groupAssets })
                                      : t('ak_dsp3_delete_the_saved_schedule_version')}
                                    className="text-[10px] px-1.5 py-1 inline-flex items-center gap-1 shrink-0 cursor-pointer"
                                    style={{
                                      borderRadius: 8,
                                      // Підтвердження видалення — бурштин, не червоний: за догмою
                                      // --err лишається за реальним збоєм, а це попередження.
                                      background: confirming ? 'color-mix(in srgb, var(--warn) 14%, transparent)' : 'transparent',
                                      border: `1px solid ${confirming ? 'var(--warn)' : 'var(--line)'}`,
                                      color: confirming ? 'var(--warn)' : 'var(--text-muted)',
                                    }}>
                                    {confirming
                                      ? <AlertTriangle size={11} strokeWidth={2} />
                                      : <Trash2 size={11} strokeWidth={2} />}
                                    {confirming
                                      ? ((records?.groupSize || 1) > 1 ? t('ak_dsp3_delete_for_the_whole_group') : t('ak_dsp3_confirm'))
                                      : ''}
                                  </button>
                                ) : (
                                  <span title={t('ak_dsp3_the_schedule_was_already_submitted_the')}
                                    className="inline-flex items-center shrink-0" style={{ color: 'var(--text-dim)' }}>
                                    <Lock size={11} strokeWidth={2} />
                                  </span>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] mt-2 pt-2" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
                {t('ak_dsp3_select_an_entry_its_hourly_values_will')}
              </div>
            </div>
          )}

          {/* ── архів версій ───────────────────────────────────────────── */}
          {showArchive && (
            <div className="tile p-4 sm:p-5 overflow-x-auto">
              <div className="flex items-center gap-2 mb-2">
                <Archive size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
                <h4 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>
                  {t('ak_dsp3_version_history_for', { d: date.split('-').reverse().join('.') })}
                </h4>
              </div>
              <table style={{ borderCollapse: 'collapse', minWidth: 780, width: '100%' }}>
                <thead>
                  <tr>
                    {[t('ak_dsp3_version_3'), t('ak_dsp3_state'), 'XML', t('ak_dsp3_generated'), t('ak_dsp3_submitted'), t('ak_dsp3_received'), t('ak_dsp3_processed'), t('ak_dsp3_status'), t('ak_dsp3_by')].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '7px 9px', fontSize: 10,
                                           color: 'var(--text-muted)', background: 'var(--surface-2)',
                                           borderBottom: '1px solid var(--line)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.versions.map((v) => (
                    <tr key={v.revision}>
                      <td style={{ padding: '7px 9px', fontSize: 12, color: 'var(--text)', borderBottom: '1px solid var(--line)' }}>{v.revision} · {v.name}</td>
                      <td style={{ padding: '7px 9px', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--line)' }}>{v.timeState.label}</td>
                      <td style={{ padding: '7px 9px', fontSize: 12, color: techColor(v.techStatus.code, 'var(--text-dim)'), borderBottom: '1px solid var(--line)' }}>{v.techStatus.label}</td>
                      {(['generated', 'submitted', 'received', 'processed', 'status'] as const).map((k) => (
                        <td key={k} style={{ padding: '7px 9px', fontSize: 12, color: v.events[k] ? 'var(--text)' : 'var(--text-dim)', borderBottom: '1px solid var(--line)' }}>{v.events[k] || '—'}</td>
                      ))}
                      <td style={{ padding: '7px 9px', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--line)' }}>{v.submittedBy || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
